'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TimeZoneCalculator() {
  const t = useTranslations("tools.timeZoneCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    date: '',
    time: '',
    fromTimezone: '+00:00',
    toTimezone: '-05:00'
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Initialize with current date and time
  useEffect(() => {
    const today = new Date();
    const dateValue = today.toISOString().split('T')[0];
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');
    const seconds = String(today.getSeconds()).padStart(2, '0');
    const timeValue = `${hours}:${minutes}:${seconds}`;

    setInputs(prev => ({
      ...prev,
      date: dateValue,
      time: timeValue
    }));
  }, []);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Timezone data
  const timezones = [
    { value: '-12:00', label: '(UTC-12:00) International Date Line West' },
    { value: '-11:00', label: '(UTC-11:00) Midway Island, Samoa' },
    { value: '-10:00', label: '(UTC-10:00) Hawaii' },
    { value: '-09:00', label: '(UTC-09:00) Alaska' },
    { value: '-08:00', label: '(UTC-08:00) Pacific Time (US/Canada), Tijuana' },
    { value: '-07:00', label: '(UTC-07:00) Mountain Time (US/Canada), Chihuahua' },
    { value: '-06:00', label: '(UTC-06:00) Central Time (US/Canada), Mexico City' },
    { value: '-05:00', label: '(UTC-05:00) Eastern Time (US/Canada), Colombia' },
    { value: '-04:00', label: '(UTC-04:00) Atlantic Time (Canada), Caracas, Santiago' },
    { value: '-03:00', label: '(UTC-03:00) Brasilia, Buenos Aires, Greenland' },
    { value: '-02:00', label: '(UTC-02:00) Mid-Atlantic' },
    { value: '-01:00', label: '(UTC-01:00) Azores, Cape Verde Islands' },
    { value: '+00:00', label: '(UTC+00:00) London, Dublin, Lisbon, Reykjavik' },
    { value: '+01:00', label: '(UTC+01:00) Paris, Berlin, Rome, Madrid' },
    { value: '+02:00', label: '(UTC+02:00) Athens, Cairo, Helsinki, Istanbul' },
    { value: '+03:00', label: '(UTC+03:00) Moscow, Baghdad, Nairobi, Kuwait' },
    { value: '+03:30', label: '(UTC+03:30) Tehran' },
    { value: '+04:00', label: '(UTC+04:00) Dubai, Baku, Tbilisi' },
    { value: '+04:30', label: '(UTC+04:30) Kabul' },
    { value: '+05:00', label: '(UTC+05:00) Islamabad, Karachi, Tashkent' },
    { value: '+05:30', label: '(UTC+05:30) Mumbai, New Delhi, Colombo' },
    { value: '+05:45', label: '(UTC+05:45) Kathmandu' },
    { value: '+06:00', label: '(UTC+06:00) Dhaka, Almaty, Astana' },
    { value: '+06:30', label: '(UTC+06:30) Yangon' },
    { value: '+07:00', label: '(UTC+07:00) Bangkok, Jakarta, Hanoi' },
    { value: '+08:00', label: '(UTC+08:00) Beijing, Singapore, Hong Kong' },
    { value: '+09:00', label: '(UTC+09:00) Tokyo, Seoul, Osaka' },
    { value: '+09:30', label: '(UTC+09:30) Adelaide, Darwin' },
    { value: '+10:00', label: '(UTC+10:00) Sydney, Melbourne, Brisbane' },
    { value: '+11:00', label: '(UTC+11:00) Solomon Islands, New Caledonia' },
    { value: '+12:00', label: '(UTC+12:00) Auckland, Fiji, Kamchatka' },
    { value: '+13:00', label: '(UTC+13:00) Nuku\'alofa, Samoa' }
  ];

  // Parse timezone offset to minutes
  const parseOffset = (offsetStr: string): number => {
    const sign = offsetStr.charAt(0) === '+' ? 1 : -1;
    const parts = offsetStr.substring(1).split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return sign * (hours * 60 + minutes);
  };

  // Format offset to string
  const formatOffset = (minutes: number): string => {
    const sign = minutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return `UTC${sign}${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Format date and time
  const formatDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const weekday = weekdays[date.getDay()];

    return `${weekday}, ${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Calculate time zone conversion
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const { date, time, fromTimezone, toTimezone } = inputs;
      const fromOffset = parseOffset(fromTimezone);
      const toOffset = parseOffset(toTimezone);

      const timeParts = time.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = timeParts[2] ? parseInt(timeParts[2], 10) : 0;

      const sourceDate = new Date(`${date}T00:00:00Z`);
      sourceDate.setUTCHours(hours);
      sourceDate.setUTCMinutes(minutes);
      sourceDate.setUTCSeconds(seconds);

      const sourceUTC = new Date(sourceDate.getTime() - fromOffset * 60000);
      const targetDate = new Date(sourceUTC.getTime() + toOffset * 60000);

      const diffMinutes = toOffset - fromOffset;
      const diffHours = Math.floor(Math.abs(diffMinutes) / 60);
      const diffMins = Math.abs(diffMinutes) % 60;
      let diffText = '';

      if (diffMinutes === 0) {
        diffText = t('sameTz');
      } else {
        const sign = diffMinutes > 0 ? '+' : '-';
        diffText = `${sign}${diffHours} ${t('hours')}`;
        if (diffMins > 0) {
          diffText += ` ${diffMins} ${t('minutes')}`;
        }
        diffText += ` (${diffMinutes > 0 ? t('ahead') : t('behind')})`;
      }

      setResults({
        sourceDateTime: formatDateTime(sourceDate),
        sourceTimezone: formatOffset(fromOffset),
        targetDateTime: formatDateTime(targetDate),
        targetTimezone: formatOffset(toOffset),
        timeDifference: diffText
      });

    } catch (err) {
      setError(t('errorMessage'));
    }
  };

  // Colors
  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' },
    { padding: '1.5rem' },
    { padding: '2rem', maxWidth: '700px' },
    { padding: '2.5rem', maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    {}
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const toolDescriptionStyle = getResponsiveStyle(
    { color: colors.textSecondary, textAlign: 'center' as const, marginBottom: '1.5rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem', marginBottom: '2rem' },
    {}
  );

  const formStyle = {
    display: 'grid',
    gap: '1.5rem'
  };

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    {}
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    {}
  );

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white'
    },
    {},
    {},
    {}
  );

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center',
    paddingRight: '2.5rem'
  };

  const hintStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginTop: '0.25rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    {}
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 600,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
      width: '100%'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.125rem' }
  );

  const errorStyle = {
    background: '#fef2f2',
    border: '2px solid #fecaca',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: error ? 'block' : 'none'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      border: '2px solid #86efac',
      display: results ? 'block' : 'none'
    },
    { marginTop: '1.5rem', padding: '1.5rem' },
    { marginTop: '2rem', padding: '2rem' },
    { marginTop: '2.5rem', padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: '#166534', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = {
    display: 'grid',
    gap: '1.5rem'
  };

  const resultCardStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)' },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    {}
  );

  const resultHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '2px solid #e2e8f0'
  };

  const resultIconStyle = getResponsiveStyle(
    {},
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultContentStyle = {
    display: 'grid',
    gap: '0.75rem'
  };

  const resultItemStyle = getResponsiveStyle(
    { background: '#f8fafc', borderRadius: '0.5rem' },
    { padding: '0.75rem', display: 'flex', flexDirection: 'column' as const, gap: '0.5rem' },
    { padding: '0.75rem', display: 'flex', flexDirection: 'row' as const, justifyContent: 'space-between', alignItems: 'center' },
    {}
  );

  const resultLabelStyle = getResponsiveStyle(
    { color: colors.textSecondary, fontWeight: 500 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    {}
  );

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { color: '#2563eb', fontWeight: 700 },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const liStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const insightCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(255, 240, 245, 0.8) 0%, rgba(255, 228, 230, 0.8) 100%)',
      border: '1px solid rgba(255, 90, 112, 0.2)',
      borderRadius: '0.75rem',
      marginBottom: '1rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem', borderRadius: '1rem' },
    { padding: '1.5rem', borderRadius: '1.25rem' }
  );

  const insightTitleStyle = getResponsiveStyle(
    { color: colors.primary, fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.75rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
      marginBottom: '1rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem', borderRadius: '1rem' },
    { padding: '1.5rem', borderRadius: '1.25rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { color: colors.primary, fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🌍 {t('toolTitle')}</h2>
            <p style={toolDescriptionStyle}>{t('toolDescription')}</p>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="input-date" style={labelStyle}>{t('dateLabel')}</label>
                <input
                  type="date"
                  id="input-date"
                  value={inputs.date}
                  onChange={(e) => setInputs({ ...inputs, date: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="input-time" style={labelStyle}>{t('timeLabel')}</label>
                <input
                  type="time"
                  id="input-time"
                  step="1"
                  value={inputs.time}
                  onChange={(e) => setInputs({ ...inputs, time: e.target.value })}
                  style={inputStyle}
                  required
                />
                <span style={hintStyle}>{t('timeHint')}</span>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="from-timezone" style={labelStyle}>{t('fromTimezoneLabel')}</label>
                <select
                  id="from-timezone"
                  value={inputs.fromTimezone}
                  onChange={(e) => setInputs({ ...inputs, fromTimezone: e.target.value })}
                  style={selectStyle}
                  required
                >
                  {timezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="to-timezone" style={labelStyle}>{t('toTimezoneLabel')}</label>
                <select
                  id="to-timezone"
                  value={inputs.toTimezone}
                  onChange={(e) => setInputs({ ...inputs, toTimezone: e.target.value })}
                  style={selectStyle}
                  required
                >
                  {timezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t('calculateButton')}
            </button>
          </form>

          <div style={errorStyle}>{error}</div>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>⏰ {t('resultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={resultCardStyle}>
                  <div style={resultHeaderStyle}>
                    <span style={resultIconStyle}>📍</span>
                    <span style={resultTitleStyle}>{t('sourceTimezoneTitle')}</span>
                  </div>
                  <div style={resultContentStyle}>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('dateTimeLabel')}</span>
                      <span style={resultValueStyle}>{results.sourceDateTime}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('timezoneLabel')}</span>
                      <span style={resultValueStyle}>{results.sourceTimezone}</span>
                    </div>
                  </div>
                </div>

                <div style={resultCardStyle}>
                  <div style={resultHeaderStyle}>
                    <span style={resultIconStyle}>🎯</span>
                    <span style={resultTitleStyle}>{t('convertedTimezoneTitle')}</span>
                  </div>
                  <div style={resultContentStyle}>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('dateTimeLabel')}</span>
                      <span style={resultValueHighlightStyle}>{results.targetDateTime}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('timezoneLabel')}</span>
                      <span style={resultValueStyle}>{results.targetTimezone}</span>
                    </div>
                  </div>
                </div>

                <div style={resultCardStyle}>
                  <div style={resultHeaderStyle}>
                    <span style={resultIconStyle}>📊</span>
                    <span style={resultTitleStyle}>{t('timeDifferenceTitle')}</span>
                  </div>
                  <div style={resultContentStyle}>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('offsetLabel')}</span>
                      <span style={resultValueStyle}>{results.timeDifference}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Introduction */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={liStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Desc')}</li>
        </ol>
      </section>

      {/* AI Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>
            <span>🤖</span>
            <span>{t('insight1Title')}</span>
          </h3>
          <p style={paragraphStyle}>{t('insight1Desc')}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>
            <span>⚡</span>
            <span>{t('insight2Title')}</span>
          </h3>
          <p style={paragraphStyle}>{t('insight2Desc')}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>
            <span>🌐</span>
            <span>{t('insight3Title')}</span>
          </h3>
          <p style={paragraphStyle}>{t('insight3Desc')}</p>
        </div>

        <div style={insightCardStyle}>
          <h3 style={insightTitleStyle}>
            <span>📅</span>
            <span>{t('insight4Title')}</span>
          </h3>
          <p style={paragraphStyle}>{t('insight4Desc')}</p>
        </div>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('detailsSubtitle1')}
        </h3>
        <p style={paragraphStyle}>{t('detailsPara1')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('detailsSubtitle2')}
        </h3>
        <p style={paragraphStyle}>{t('detailsPara2')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem' }}>
          {t('detailsSubtitle3')}
        </h3>
        <ul style={listStyle}>
          <li style={liStyle}>{t('detailsList1')}</li>
          <li style={liStyle}>{t('detailsList2')}</li>
          <li style={liStyle}>{t('detailsList3')}</li>
          <li style={liStyle}>{t('detailsList4')}</li>
          <li style={liStyle}>{t('detailsList5')}</li>
        </ul>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={liStyle}><strong style={strongStyle}>{t('practice1Title')}</strong> {t('practice1Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('practice2Title')}</strong> {t('practice2Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('practice3Title')}</strong> {t('practice3Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('practice4Title')}</strong> {t('practice4Desc')}</li>
          <li style={liStyle}><strong style={strongStyle}>{t('practice5Title')}</strong> {t('practice5Desc')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={liStyle}>
            <a href="https://sickrate.com/time-zone-converter" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Time Zone Converter | World Time Calculator - Sickrate
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://easy.jobs/blog/best-time-zone-converter-tools-for-remote-teams/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              5 Best Time Zone Converter Tools For Remote Teams - Easy.jobs
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://blog.teamup.com/10-best-tools-for-time-zone-conversion/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              10 Best Tools for Time Zone Conversion - Teamup Blog
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://www.timetrex.com/resources/usa-time-zone-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              US Time Zone Calculator - TimeTrex
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://poskod.sg/essential-time-conversion-tools-for-a-globalized-world/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              Essential Time Conversion Tools for a Globalized World - Poskod.sg
            </a>
          </li>
          <li style={liStyle}>
            <a href="https://dialzara.com/blog/ai-time-zone-conversion-for-scheduling-guide" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              AI Time Zone Conversion for Scheduling: Guide - Dialzara
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}